import { Plus, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useWizard } from "../../context/WizardContext";
import { PERSON_TYPE } from "../../types/individual.types";
import { INDIVIDUAL_FILING_STATUS, RELATION_WITH_TAXPAYER } from "../../types/individual.types";
import { US_STATE_CODES } from "../../types/wizard.types";
import type { PersonalInfo } from "../../types/individual.types";
import type { AddressInformation } from "../../types/individual.types";
import { MaskedSsnInput } from "../shared/MaskedSsnInput";
import { DateInput } from "../shared/DateInput";
import { cn } from "@/lib/utils";

const FILING_STATUS_LABELS: Record<string, string> = {
  [INDIVIDUAL_FILING_STATUS.SINGLE]: "Single",
  [INDIVIDUAL_FILING_STATUS.MARRIED_FILING_JOINTLY]: "Married Filing Jointly (MFJ)",
  [INDIVIDUAL_FILING_STATUS.MARRIED_FILING_SEPARATELY]: "Married Filing Separately (MFS)",
  [INDIVIDUAL_FILING_STATUS.HEAD_OF_HOUSEHOLD]: "Head of Household (HOH)",
  [INDIVIDUAL_FILING_STATUS.QUALIFYING_WIDOW]: "Qualifying Widow(er) (QW)",
};

function getPerson(list: PersonalInfo[] | undefined, type: "TAXPAYER" | "SPOUSE" | "DEPENDENT", index?: number): PersonalInfo | undefined {
  if (!list) return undefined;
  if (type === "TAXPAYER") return list.find((p) => p.personType === "TAXPAYER");
  if (type === "SPOUSE") return list.find((p) => p.personType === "SPOUSE");
  if (type === "DEPENDENT") {
    const deps = list.filter((p) => p.personType === "DEPENDENT");
    return index !== undefined ? deps[index] : deps[0];
  }
  return undefined;
}

function ensurePersonList(list: PersonalInfo[] | undefined, type: "TAXPAYER" | "SPOUSE" | "DEPENDENT", index?: number): PersonalInfo {
  const arr = list ?? [];
  if (type === "TAXPAYER") {
    let p = arr.find((x) => x.personType === "TAXPAYER");
    if (!p) {
      p = { personType: "TAXPAYER" };
      arr.push(p);
    }
    return p;
  }
  if (type === "SPOUSE") {
    let p = arr.find((x) => x.personType === "SPOUSE");
    if (!p) {
      p = { personType: "SPOUSE" };
      arr.push(p);
    }
    return p;
  }
  if (type === "DEPENDENT" && index !== undefined) {
    const deps = arr.filter((x) => x.personType === "DEPENDENT");
    while (deps.length <= index) {
      deps.push({ personType: "DEPENDENT" });
    }
    return deps[index];
  }
  return { personType: "TAXPAYER" };
}

export function IndividualStep1Personal() {
  const { individualData, updateIndividualData } = useWizard();
  const list = individualData.personalInformation ?? [];
  const addr = individualData.addressInformation ?? {};
  const hasSpouse = list.some((p) => p.personType === "SPOUSE");
  const dependents = list.filter((p) => p.personType === "DEPENDENT");
  const numDependents = dependents.length;

  const updateList = (next: PersonalInfo[]) => {
    updateIndividualData({ personalInformation: next });
  };

  const setPerson = (type: "TAXPAYER" | "SPOUSE" | "DEPENDENT", index: number | undefined, patch: Partial<PersonalInfo>) => {
    const arr = [...list];
    const p = ensurePersonList(arr, type, index);
    Object.assign(p, patch);
    if (type === "SPOUSE" && !arr.some((x) => x.personType === "SPOUSE")) {
      arr.push({ ...p, personType: "SPOUSE" });
    }
    if (type === "DEPENDENT" && index !== undefined) {
      const deps = arr.filter((x) => x.personType === "DEPENDENT");
      while (deps.length <= index) deps.push({ personType: "DEPENDENT" });
      Object.assign(deps[index], patch);
    }
    updateList(arr);
  };

  const setHasSpouse = (yes: boolean) => {
    if (yes && !list.some((p) => p.personType === "SPOUSE")) {
      updateList([...list, { personType: "SPOUSE" }]);
    }
    if (!yes) {
      updateList(list.filter((p) => p.personType !== "SPOUSE"));
    }
  };

  const addDependent = () => {
    updateList([...list, { personType: "DEPENDENT" }]);
  };

  const removeDependent = (index: number) => {
    const deps = list.filter((p) => p.personType === "DEPENDENT");
    const rest = list.filter((p) => p.personType !== "DEPENDENT");
    const next = [...rest, ...deps.filter((_, i) => i !== index)];
    updateList(next);
  };

  const setAddr = (patch: Partial<AddressInformation>) => {
    updateIndividualData({ addressInformation: { ...addr, ...patch } });
  };

  const taxpayer = getPerson(list, "TAXPAYER") ?? { personType: "TAXPAYER" as const };
  const spouse = getPerson(list, "SPOUSE");
  const filingStatus = individualData.filingStatus;
  const showSpouseUi =
    filingStatus !== INDIVIDUAL_FILING_STATUS.SINGLE &&
    filingStatus !== INDIVIDUAL_FILING_STATUS.HEAD_OF_HOUSEHOLD;

  const setFilingStatus = (status: keyof typeof INDIVIDUAL_FILING_STATUS) => {
    const hideSpouse =
      status === INDIVIDUAL_FILING_STATUS.SINGLE ||
      status === INDIVIDUAL_FILING_STATUS.HEAD_OF_HOUSEHOLD;
    if (hideSpouse && hasSpouse) {
      updateIndividualData({
        filingStatus: status,
        personalInformation: list.filter((p) => p.personType !== "SPOUSE"),
      });
      return;
    }
    updateIndividualData({ filingStatus: status });
  };

  const addressTabs = [
    { value: "taxpayer", label: "Taxpayer" },
    ...(hasSpouse && showSpouseUi ? [{ value: "spouse", label: "Spouse" }] : []),
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Personal Information</h2>
        <p className="text-sm text-muted-foreground">
          Taxpayer details, family members, and address.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Taxpayer Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="tp-first">First Name <span className="text-destructive">*</span></Label>
              <Input
                id="tp-first"
                value={taxpayer.firstName ?? ""}
                onChange={(e) => setPerson("TAXPAYER", undefined, { firstName: e.target.value })}
                placeholder="First name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tp-last">Last Name <span className="text-destructive">*</span></Label>
              <Input
                id="tp-last"
                value={taxpayer.lastName ?? ""}
                onChange={(e) => setPerson("TAXPAYER", undefined, { lastName: e.target.value })}
                placeholder="Last name"
              />
            </div>
          </div>
          <DateInput
            label="Date of Birth (required)"
            value={taxpayer.dateOfBirth}
            onChange={(v) => setPerson("TAXPAYER", undefined, { dateOfBirth: v })}
            required
          />
          <MaskedSsnInput
            label="SSN / ITIN"
            value={taxpayer.ssn ?? ""}
            onChange={(v) => setPerson("TAXPAYER", undefined, { ssn: v })}
          />
          <div className="space-y-2">
            <Label>Type of Visa <span className="text-destructive">*</span></Label>
            <Select
              value={taxpayer.immigrationDetails?.visaType ?? ""}
              onValueChange={(v) =>
                setPerson("TAXPAYER", undefined, {
                  immigrationDetails: { ...taxpayer.immigrationDetails, visaType: v },
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select visa type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="B1/B2">B1/B2</SelectItem>
                <SelectItem value="H1B">H1B</SelectItem>
                <SelectItem value="L1">L1</SelectItem>
                <SelectItem value="F1">F1</SelectItem>
                <SelectItem value="H1">H1</SelectItem>
                <SelectItem value="Citizen">Citizen</SelectItem>
                <SelectItem value="Green Card">Green Card</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DateInput
            label="First date of entry in USA"
            value={taxpayer.immigrationDetails?.firstDateOfEntry}
            onChange={(v) =>
              setPerson("TAXPAYER", undefined, {
                immigrationDetails: { ...taxpayer.immigrationDetails, firstDateOfEntry: v },
              })
            }
          />
          <div className="space-y-2">
            <Label htmlFor="tp-occupation">Occupation <span className="text-destructive">*</span></Label>
            <Input
              id="tp-occupation"
              value={taxpayer.occupation ?? ""}
              onChange={(e) => setPerson("TAXPAYER", undefined, { occupation: e.target.value })}
              placeholder="Occupation"
            />
          </div>
          <div className="space-y-2">
            <Label>Marital / Filing Status <span className="text-destructive">*</span></Label>
            <Select
              value={filingStatus ?? ""}
              onValueChange={(v) => setFilingStatus(v as keyof typeof INDIVIDUAL_FILING_STATUS)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(FILING_STATUS_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Family Members</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {showSpouseUi && (
            <>
              <div className="space-y-2">
                <Label>Do you have a spouse?</Label>
                <RadioGroup
                  value={hasSpouse ? "yes" : "no"}
                  onValueChange={(v) => setHasSpouse(v === "yes")}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="spouse-yes" />
                    <Label htmlFor="spouse-yes" className="font-normal">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="spouse-no" />
                    <Label htmlFor="spouse-no" className="font-normal">No</Label>
                  </div>
                </RadioGroup>
              </div>
              {hasSpouse && spouse && (
                <div className="rounded-lg border border-border p-4 space-y-4">
                  <p className="text-sm font-medium">Spouse details (required when married)</p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>First Name <span className="text-destructive">*</span></Label>
                      <Input
                        value={spouse.firstName ?? ""}
                        onChange={(e) => setPerson("SPOUSE", undefined, { firstName: e.target.value })}
                        placeholder="First name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Last Name <span className="text-destructive">*</span></Label>
                      <Input
                        value={spouse.lastName ?? ""}
                        onChange={(e) => setPerson("SPOUSE", undefined, { lastName: e.target.value })}
                        placeholder="Last name"
                      />
                    </div>
                  </div>
                  <DateInput
                    label="DOB (required)"
                    value={spouse.dateOfBirth}
                    onChange={(v) => setPerson("SPOUSE", undefined, { dateOfBirth: v })}
                    required
                  />
                  <MaskedSsnInput
                    label="SSN / ITIN"
                    value={spouse.ssn ?? ""}
                    onChange={(v) => setPerson("SPOUSE", undefined, { ssn: v })}
                  />
                  <div className="space-y-2">
                    <Label>Relation to Taxpayer</Label>
                    <Input value="Spouse" readOnly className="bg-muted" />
                  </div>
                  <div className="space-y-2">
                    <Label>Occupation</Label>
                    <Input
                      value={spouse.occupation ?? ""}
                      onChange={(e) => setPerson("SPOUSE", undefined, { occupation: e.target.value })}
                      placeholder="Occupation"
                    />
                  </div>
                </div>
              )}
            </>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Label className="w-full shrink-0">Dependents</Label>
            <Button type="button" variant="outline" size="sm" onClick={addDependent} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Add dependent
            </Button>
          </div>
          {numDependents > 0 && (
            <div className="space-y-4">
              {dependents.map((dep, i) => (
                <div key={i} className="rounded-lg border border-border p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">Dependent {i + 1}</p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => removeDependent(i)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>First Name</Label>
                      <Input
                        value={dep.firstName ?? ""}
                        onChange={(e) => setPerson("DEPENDENT", i, { firstName: e.target.value })}
                        placeholder="First name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Last Name</Label>
                      <Input
                        value={dep.lastName ?? ""}
                        onChange={(e) => setPerson("DEPENDENT", i, { lastName: e.target.value })}
                        placeholder="Last name"
                      />
                    </div>
                  </div>
                  <DateInput
                    label="DOB"
                    value={dep.dateOfBirth}
                    onChange={(v) => setPerson("DEPENDENT", i, { dateOfBirth: v })}
                  />
                  <MaskedSsnInput
                    label="SSN / ITIN"
                    value={dep.ssn ?? ""}
                    onChange={(v) => setPerson("DEPENDENT", i, { ssn: v })}
                  />
                  <div className="space-y-2">
                    <Label>Relation to Taxpayer</Label>
                    <Select
                      value={dep.relationWithTaxPayer ?? ""}
                      onValueChange={(v) => setPerson("DEPENDENT", i, { relationWithTaxPayer: v as keyof typeof RELATION_WITH_TAXPAYER })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(RELATION_WITH_TAXPAYER).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Occupation</Label>
                    <Input
                      value={dep.occupation ?? ""}
                      onChange={(e) => setPerson("DEPENDENT", i, { occupation: e.target.value })}
                      placeholder="Occupation"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Address (required)</CardTitle>
          <p className="text-sm text-muted-foreground">
            Current address (household). Street, city, state, and ZIP are required.
          </p>
        </CardHeader>
        <CardContent>
          {addressTabs.length > 1 ? (
            <Tabs defaultValue="taxpayer" className="w-full">
              <TabsList className="mb-4 border border-primary/30 bg-primary/5">
                {addressTabs.map((t) => (
                  <TabsTrigger
                    key={t.value}
                    value={t.value}
                    className={
                      t.value === "taxpayer" || t.value === "spouse"
                        ? "data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm data-[state=active]:border-black"
                        : undefined
                    }
                  >
                    {t.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              {addressTabs.map((t) => (
                <TabsContent key={t.value} value={t.value} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor={`addr-street-${t.value}`}>Street Address <span className="text-destructive">*</span></Label>
                    <Input
                      id={`addr-street-${t.value}`}
                      value={addr.currentAddress ?? ""}
                      onChange={(e) => setAddr({ currentAddress: e.target.value })}
                      placeholder="Street address"
                    />
                  </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor={`addr-city-${t.value}`}>City <span className="text-destructive">*</span></Label>
                      <Input
                        id={`addr-city-${t.value}`}
                        value={addr.city ?? ""}
                        onChange={(e) => setAddr({ city: e.target.value })}
                        placeholder="City"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>State <span className="text-destructive">*</span></Label>
                      <Select
                        value={addr.state ?? ""}
                        onValueChange={(v) => setAddr({ state: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="State" />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATE_CODES.map((code) => (
                            <SelectItem key={code} value={code}>
                              {code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor={`addr-zip-${t.value}`}>ZIP <span className="text-destructive">*</span></Label>
                      <Input
                        id={`addr-zip-${t.value}`}
                        value={addr.zip ?? ""}
                        onChange={(e) => setAddr({ zip: e.target.value })}
                        placeholder="ZIP"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`addr-country-${t.value}`}>Country</Label>
                      <Input id={`addr-country-${t.value}`} value="USA" readOnly className="bg-muted" />
                    </div>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="addr-street">Street Address <span className="text-destructive">*</span></Label>
                <Input
                  id="addr-street"
                  value={addr.currentAddress ?? ""}
                  onChange={(e) => setAddr({ currentAddress: e.target.value })}
                  placeholder="Street address"
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="addr-city">City <span className="text-destructive">*</span></Label>
                  <Input
                    id="addr-city"
                    value={addr.city ?? ""}
                    onChange={(e) => setAddr({ city: e.target.value })}
                    placeholder="City"
                  />
                </div>
                <div className="space-y-2">
                  <Label>State <span className="text-destructive">*</span></Label>
                  <Select value={addr.state ?? ""} onValueChange={(v) => setAddr({ state: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="State" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATE_CODES.map((code) => (
                        <SelectItem key={code} value={code}>
                          {code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="addr-zip">ZIP <span className="text-destructive">*</span></Label>
                  <Input
                    id="addr-zip"
                    value={addr.zip ?? ""}
                    onChange={(e) => setAddr({ zip: e.target.value })}
                    placeholder="ZIP"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="addr-country">Country</Label>
                  <Input id="addr-country" value="USA" readOnly className="bg-muted" />
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
